'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';

import Icon, { type IconType } from '@commons/components/Icon';
import Link from '@commons/components/Link';
import Flex, { type FlexProps } from '@commons/containers/Flex';
import { asAbsoluteURL } from '@commons/helpers/url';
import { DOMAIN } from '@commons/utils/constants';

import styles from './index.module.scss';

/**
 * The types of supported share links.
 */

export type ShareLinkType = 'facebook' | 'x' | 'linkedin' | 'email' | 'pagelink';

// Type definition for the ShareLinks component properties.

type Props = FlexProps & {
  links: ShareLinkType[];
  emailSubject?: string;
  emailBody?: string;
};

/**
 * ShareLinks component.
 *
 * Renders a set of share links based on the current page URL.
 */

const ShareLinks = ({ links = [], emailSubject, emailBody, className, ...props }: Props) => {
  const t = useTranslations('commons.shareLinks');
  const pathname = usePathname();
  const pageUrl = asAbsoluteURL(pathname, { domain: DOMAIN });

  const shareLinks = generateShareLinks(pageUrl, emailSubject, emailBody);

  const renderedLinks = links.map((link) => {
    if (!shareLinks[link]) return null;

    const icon = shareLinks[link].icon as IconType;
    if (link === 'pagelink') {
      return (
        <button
          key={link}
          type="button"
          className={clsx('link', styles.copyButton)}
          onClick={() => copyToClipboard(pageUrl)}
          aria-label={t(link)}
        >
          <Icon type={icon} />
        </button>
      );
    }

    return (
      <Link key={link} href={shareLinks[link].url} aria-label={t(link)}>
        <Icon type={icon} />
      </Link>
    );
  });

  const classes = clsx(styles.shareLinks, className);

  return (
    <Flex gap="sm" align="center" direction="row" className={classes} {...props}>
      {renderedLinks}
    </Flex>
  );
};

/**
 * Generates the share links based on the page URL.
 *
 * @param pageUrl - The absolute URL of the current page.
 * @param emailSubject - The subject for the email share link.
 * @param emailBody - The body for the email share link.
 *
 * @returns The share links with their respective URLs and icons.
 */

const generateShareLinks = (pageUrl: string, emailSubject?: string, emailBody?: string) => {
  const encodedPageLink = encodeURIComponent(pageUrl);
  const encodedEmailSubject = encodeURIComponent(emailSubject || 'Crisis24');

  let emailUrl = `mailto:?subject=${encodedEmailSubject}`;

  if (emailBody) {
    const encodedEmailBody = encodeURIComponent(emailBody) + '%0A%0A' + encodeURIComponent(pageUrl);
    emailUrl += `&body=${encodedEmailBody}`;
  }

  return {
    facebook: {
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedPageLink}`,
      icon: 'facebook',
    },
    x: {
      url: `https://twitter.com/intent/tweet?url=${encodedPageLink}`,
      icon: 'x',
    },
    linkedin: {
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedPageLink}`,
      icon: 'linkedin',
    },
    pagelink: {
      url: '#',
      icon: 'link',
    },
    email: {
      url: emailUrl,
      icon: 'email',
    },
  };
};

/**
 * Helper function to copy text to the clipboard.
 *
 * @param text - The text to be copied to the clipboard.
 */

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(
    () => {
      alert('Link copied to clipboard!');
    },
    (err) => {
      console.error('Could not copy text: ', err);
    }
  );
};

export default ShareLinks;
